import React, { useState, useEffect } from 'react';
import ResultOfAptMetrixContainer from '../components/ResultOfAptMetrixContainer';
import { columnOrderForAptA1B1Info, columnOrderForAptMetrix } from '../constant/columnsName';
import SaleModal from '../components/SaleModal';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useApiContext } from '../context/ApiContext';
import { useAptMetrixContext } from '../context/AptMetrixContext';
import { checkSubscriptionStatus } from '../util/subscriptionUtils';

function AptMetrix() {
  const navigate = useNavigate();
  const { session } = useAppContext();
  const { api, isLoading: apiLoading } = useApiContext();
  
  // AptMetrixContext에서 필요한 상태들을 가져옴
  const { 
    selectedRegionInfo, setSelectedRegionInfo,
    aptMetrixInfo, setAptMetrixInfo,
    isFetchingAptMetrixInfo, setIsFetchingAptMetrixInfo,
    aptMetrixInfoFetchError, setAptMetrixInfoFetchError
  } = useAptMetrixContext();
  
  const [aptNo, setAptNo] = useState(null);
  const [aptA1B1ListInfo, setAptA1B1ListInfo] = useState(null);
  const [isFetchingAptA1B1Info, setIsFetchingAptA1B1Info] = useState(false);
  const [aptA1B1InfoFetchError, setAptA1B1InfoFetchError] = useState(false);

  // 구독 및 무료체험 상태 관리
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [freeTrialInfo, setFreeTrialInfo] = useState(null);

  // 모달 관련 상태 추가
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [modalIsRent, setModalIsRent] = useState(false);

  // 평형 정보 수집
  const [pyeongName, setPyeongName] = useState('');
  
  // 모달이 열릴 때 스크롤바 공간 유지하면서 스크롤 방지
  useEffect(() => {
    if (showSaleModal) {
      // 스크롤바 너비 계산
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // 스크롤 방지 및 패딩 추가로 레이아웃 시프트 방지
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      // 원래 상태로 복원
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    
    // 컴포넌트 언마운트 시 스크롤 복원
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showSaleModal]);
  
  // 구독 상태 확인을 위한 useEffect
  useEffect(() => {
    async function checkStatus() {
      setLoading(true);
      const status = await checkSubscriptionStatus(session);
      
      setLoading(status.loading);
      setHasAccess(status.hasAccess);
      setSubscription(status.subscription);
      setFreeTrialInfo(status.freeTrialInfo);
    }

    checkStatus();
  }, [session]);


  // 지역 선택 핸들러 - 다중 선택 지원
  const handleRegionSelect = (regionData) => {
    console.log("지역 선택 데이터:", regionData);
    
    // 여러 지역 선택 여부 확인
    const isMultipleRegions = regionData.multipleRegions || false;
    
    // 선택된 도시 정보
    const selectedCities = regionData.selectedCities || [];
    
    // 지역 이름 정보 (다중 지역 지원)
    const regionNames = regionData.regionNames || {};
    
    // 기존 데이터와 새 데이터 병합
    setSelectedRegionInfo({
      ...selectedRegionInfo,
      ...regionData,
      multipleRegions: isMultipleRegions,
      regionNames: regionNames
    });
    
    // 검색 버튼이 클릭된 경우에만 API 호출
    if (regionData.searchClicked || regionData.autoSearch) {
      console.log("검색 트리거됨:", regionData);
      getAptMetrixInfo(regionData);
    }
  };

  const getAptListA1B1Info = async (apt_no, pyeongNo, isRent) => {
    setIsFetchingAptA1B1Info(true);
    
    try {
      const { data, error } = await api.getAptSaleInfo(apt_no, pyeongNo, isRent);
      
      if (error) {
        console.error('아파트 매물 정보 조회 오류:', error);
        setAptA1B1InfoFetchError(true);
        setAptA1B1ListInfo(null);
        return;
      }
      
      setAptA1B1ListInfo(data);
      setAptA1B1InfoFetchError(false);
    } catch (error) {
      console.error('아파트 매물 정보 조회 중 오류 발생:', error);
      setAptA1B1InfoFetchError(true);
      setAptA1B1ListInfo(null);
    } finally {
      setIsFetchingAptA1B1Info(false);
    }
  };

  const getAptMetrixInfo = async (regionInfo = selectedRegionInfo) => {
    console.log("API 호출 시작:", regionInfo);
    setIsFetchingAptMetrixInfo(true);
    
    try {
      // 다중 지역 선택 지원 API 호출
      const { data, error } = await api.getAptMetrixInfo(regionInfo);
      
      if (error) {
        console.error('아파트 메트릭스 정보 조회 오류:', error);
        setAptMetrixInfoFetchError(true);
        setAptMetrixInfo(null);
        return;
      }
      
      console.log("API 응답 데이터:", data?.length || 0, "건");
      setAptMetrixInfo(data);
      setAptMetrixInfoFetchError(false);
    } catch (error) {
      console.error('아파트 메트릭스 정보 조회 중 오류 발생:', error);
      setAptMetrixInfoFetchError(true);
      setAptMetrixInfo(null);
    } finally {
      setIsFetchingAptMetrixInfo(false);
    }
  };

  // 버튼 클릭 시 모달을 열고, 해당 매물 정보를 불러옴
  const handleSaleClick = (complexNo, pyeongNo, isRent, pyeongName) => {
    setAptNo(complexNo);
    setModalIsRent(isRent);
    getAptListA1B1Info(complexNo, pyeongNo, isRent);
    setShowSaleModal(true);
    setPyeongName(pyeongName);
  };

  // 로딩 중인 경우
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8 h-[60vh]">
        <p className="text-xl">로딩 중...</p>
      </div>
    );
  }

  // 미로그인 또는 구독이 없는 경우 접근 제한 메시지 표시
  if (!hasAccess) {
    return (
      <div className="flex flex-col justify-center items-center p-4 h-[60vh]">
        <h2 className="text-2xl font-bold mb-4">이 기능을 사용하려면 구독이 필요합니다</h2>
        <p className="text-gray-600 mb-6 text-center">
          AptMetrix 기능은 유료 구독 회원 또는 무료체험 기간 중에만 이용할 수 있습니다.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button 
            onClick={() => navigate('/price')} 
            className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition-colors">
            구독 신청하기
          </button>
          <button 
            onClick={() => navigate('/')} 
            className="bg-gray-200 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-300 transition-colors">
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="m-1 p-1.5 max-w-full overflow-x-hidden">
      <ResultOfAptMetrixContainer 
        isFetchingAptMetrixInfo={isFetchingAptMetrixInfo}
        aptMetrixInfoFetchError={aptMetrixInfoFetchError}
        aptMetrixInfo={aptMetrixInfo}
        columnOrderForAptMetrix={columnOrderForAptMetrix}
        onSaleClick={handleSaleClick}
        onRegionSelect={handleRegionSelect}
      />

      <SaleModal 
        showSaleModal={showSaleModal}
        modalIsRent={modalIsRent}
        setShowSaleModal={setShowSaleModal}
        isFetchingAptA1B1Info={isFetchingAptA1B1Info}
        aptA1B1InfoFetchError={aptA1B1InfoFetchError}
        aptA1B1ListInfo={aptA1B1ListInfo}
        columnOrderForAptA1B1Info={columnOrderForAptA1B1Info}
        aptNo={aptNo}
        pyeongName={pyeongName}
      />
    </div>
  );
}

export default AptMetrix; 